<p-progressBar mode="indeterminate" *ngIf="isLoading"></p-progressBar>

<app-page-shell *ngIf="!isLoading && loadError">
    <p role="alert">{{ loadError }}</p>
    <p-button label="Back to Vehicles" icon="pi pi-arrow-left" (onClick)="backToVehicles()"></p-button>
</app-page-shell>

<app-page-shell *ngIf="!isLoading && !loadError">

    <app-page-detail-hero
        eyebrow="Vehicle"
        [title]="vehicle.plateNumber || ('Vehicle # ' + vehicleId)"
        [subtitle]="vehicle.registrationNumber || 'Vehicle profile'"
        icon="fa-solid fa-truck">
        <div tags>
            <p-tag [value]="vehicle.isActive === false ? 'Inactive' : 'Active'" [severity]="vehicle.isActive === false ? 'danger' : 'success'"></p-tag>
            <p-tag [value]="vehicle.validated ? 'Validated' : 'Validation pending'" [severity]="vehicle.validated ? 'success' : 'warn'"></p-tag>
            <p-tag *ngIf="vehicle.resourceCompanyName" [value]="vehicle.resourceCompanyName" severity="secondary"></p-tag>
        </div>
        <div actions>
            <p-button label="Back" icon="pi pi-arrow-left" severity="secondary" [outlined]="true" (onClick)="backToVehicles()"></p-button>
            <p-button label="Track" icon="fa-solid fa-map-location" severity="success" [outlined]="true" (onClick)="trackVehicle()"></p-button>
            <p-button *ngIf="!isReadOnlyResource" label="Edit" icon="pi pi-pencil" (onClick)="editVehicle()"></p-button>
            <p-button *ngIf="!isReadOnlyResource" label="Change ownership" icon="pi pi-sync" severity="warn" [outlined]="true" (onClick)="changeOwnership()"></p-button>
        </div>
    </app-page-detail-hero>

    <app-page-metric-strip [metrics]="vehicleMetrics()"></app-page-metric-strip>

    <div class="detail-layout">
        <app-page-data-panel title="Vehicle Profile" subtitle="Identity, specifications, and registration data.">
            <div class="vehicle-header">
                <img [src]="heroImage()" [alt]="vehicle.plateNumber || 'Vehicle'">
                <div>
                    <strong>{{ vehicle.plateNumber || '-' }}</strong>
                    <span>{{ vehicle.type?.name || '-' }}</span>
                </div>
            </div>

            <dl class="detail-grid">
                <div><dt>Plate Number</dt><dd>{{ vehicle.plateNumber || '-' }}</dd></div>
                <div><dt>Source Company</dt><dd>{{ vehicle.resourceCompanyName || '-' }}</dd></div>
                <div><dt>Registration Number</dt><dd>{{ vehicle.registrationNumber || '-' }}</dd></div>
                <div><dt>Engine Number</dt><dd>{{ vehicle.engineNumber || '-' }}</dd></div>
                <div><dt>Chassis / VIN</dt><dd>{{ vehicle.chassisNumber || '-' }}</dd></div>
                <div><dt>Registration Country</dt><dd>{{ vehicle.registrationCountry?.name || '-' }}</dd></div>
                <div><dt>Registration City</dt><dd>{{ vehicle.registrationCity?.cityName || '-' }}</dd></div>
                <div><dt>Registration Authority</dt><dd>{{ vehicle.registrationAuthority || '-' }}</dd></div>
                <div><dt>Registration Issue</dt><dd>{{ dateLabel(vehicle.registrationIssueDate) }}</dd></div>
                <div><dt>Registration Expiry</dt><dd>{{ dateLabel(vehicle.registrationExpiry) }}</dd></div>
                <div><dt>Registration Usage</dt><dd>{{ vehicle.registrationUsage || '-' }}</dd></div>
                <div><dt>Plate Source</dt><dd>{{ vehicle.plateSource || '-' }}</dd></div>
                <div><dt>Plate Type</dt><dd>{{ vehicle.plateType || '-' }}</dd></div>
                <div><dt>Plate Color</dt><dd>{{ vehicle.plateColor || '-' }}</dd></div>
                <div><dt>Type</dt><dd>{{ vehicle.type?.name || '-' }}</dd></div>
                <div><dt>Configuration</dt><dd>{{ vehicle.config?.name || '-' }}</dd></div>
                <div><dt>Capacity</dt><dd>{{ vehicle.capacity?.name || '-' }}</dd></div>
                <div><dt>Make</dt><dd>{{ vehicle.make?.name || '-' }}</dd></div>
                <div><dt>Model</dt><dd>{{ vehicle.model?.name || '-' }}</dd></div>
                <div><dt>Year</dt><dd>{{ vehicle.year?.year || '-' }}</dd></div>
            </dl>
        </app-page-data-panel>

        <app-page-data-panel title="Compliance & Assignment" subtitle="Ownership, insurance, inspection, operating card, and driver assignment.">
            <dl class="detail-grid">
                <div><dt>Ownership Type</dt><dd>{{ vehicle.ownershipType || '-' }}</dd></div>
                <div><dt>Registered Owner</dt><dd>{{ vehicle.registeredOwnerName || '-' }}</dd></div>
                <div><dt>Owner Identity / CR</dt><dd>{{ vehicle.registeredOwnerIdentityNumber || '-' }}</dd></div>
                <div><dt>Payload Types</dt><dd>{{ payloadTypes() }}</dd></div>
                <div><dt>Trip Types</dt><dd>{{ tripTypes() }}</dd></div>
                <div><dt>Current Driver</dt><dd>{{ vehicle.currentDriver?.name || '-' }}</dd></div>
                <div><dt>Driver Phone</dt><dd>{{ vehicle.currentDriver?.phoneNumber || '-' }}</dd></div>
                <div><dt>Insured</dt><dd>{{ vehicle.isInsured ? 'Yes' : 'No' }}</dd></div>
                <div><dt>Insurance Company</dt><dd>{{ vehicle.insuranceCompany?.name || '-' }}</dd></div>
                <div><dt>Insurance Policy</dt><dd>{{ vehicle.insurancePolicyNo || '-' }}</dd></div>
                <div><dt>Insurance Amount</dt><dd>{{ vehicle.insuranceAmount || '-' }}</dd></div>
                <div><dt>Insurance Issue</dt><dd>{{ dateLabel(vehicle.insuranceIssueDate) }}</dd></div>
                <div><dt>Insurance Expiry</dt><dd>{{ dateLabel(vehicle.insuranceExpiry) }}</dd></div>
                <div><dt>Inspection Number</dt><dd>{{ vehicle.technicalInspectionNumber || '-' }}</dd></div>
                <div><dt>Inspection Issue</dt><dd>{{ dateLabel(vehicle.technicalInspectionIssueDate) }}</dd></div>
                <div><dt>Inspection Expiry</dt><dd>{{ dateLabel(vehicle.technicalInspectionExpiryDate) }}</dd></div>
                <div><dt>Operating Card</dt><dd>{{ vehicle.operatingCardNumber || '-' }}</dd></div>
                <div><dt>Operating Card Country</dt><dd>{{ vehicle.operatingCardCountry?.name || '-' }}</dd></div>
                <div><dt>Operating Card Activity</dt><dd>{{ vehicle.operatingCardActivity || '-' }}</dd></div>
                <div><dt>Operating Card Status</dt><dd>{{ vehicle.operatingCardStatus || '-' }}</dd></div>
                <div><dt>Operating Card Issue</dt><dd>{{ dateLabel(vehicle.operatingCardIssueDate) }}</dd></div>
                <div><dt>Operating Card Expiry</dt><dd>{{ dateLabel(vehicle.operatingCardExpiryDate) }}</dd></div>
            </dl>

            <div class="document-links">
                <a *ngIf="vehicle.registrationImage" [href]="vehicle.registrationImage" target="_blank">Registration Document</a>
                <a *ngIf="vehicle.insuranceDocumentUrl" [href]="vehicle.insuranceDocumentUrl" target="_blank">Insurance Document</a>
                <a *ngIf="vehicle.technicalInspectionDocumentUrl" [href]="vehicle.technicalInspectionDocumentUrl" target="_blank">Inspection Document</a>
                <a *ngIf="vehicle.operatingCardDocumentUrl" [href]="vehicle.operatingCardDocumentUrl" target="_blank">Operating Card</a>
                <a *ngFor="let image of vehicle.vehicleImages" [href]="image" target="_blank">Vehicle Image</a>
                <span *ngIf="!vehicle.registrationImage && !vehicle.insuranceDocumentUrl && !vehicle.technicalInspectionDocumentUrl && !vehicle.operatingCardDocumentUrl && (!vehicle.vehicleImages || vehicle.vehicleImages.length === 0)">No documents uploaded.</span>
            </div>
        </app-page-data-panel>

        <app-page-data-panel title="Ownership correction history" subtitle="Immutable record of operating-ownership corrections.">
            <p-table [value]="ownershipHistory" styleClass="p-datatable-sm">
                <ng-template pTemplate="header"><tr><th>When</th><th>Change</th><th>Reason</th><th>Outcome</th></tr></ng-template>
                <ng-template pTemplate="body" let-row><tr><td>{{ row.createdAt | date:'short' }}</td><td><span *ngIf="row.targetCompanyId && row.targetCompanyId !== row.owningCompanyId">Company #{{ row.owningCompanyId }} → Company #{{ row.targetCompanyId }} · </span>{{ row.sourceOwnershipType }} → {{ row.targetOwnershipType }}</td><td>{{ row.reason }}</td><td>{{ row.outcome }}</td></tr></ng-template>
                <ng-template pTemplate="emptymessage"><tr><td colspan="4">No ownership corrections recorded.</td></tr></ng-template>
            </p-table>
        </app-page-data-panel>
    </div>
</app-page-shell>

<app-fleet-ownership-transfer-dialog [(visible)]="ownershipTransferVisible" [owningCompanyId]="currentCompanyId"
    [resources]="ownershipTransferResources" [pairedResource]="ownershipTransferPairedResource"
    (completed)="onOwnershipTransferCompleted($event)"></app-fleet-ownership-transfer-dialog>
